import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useServiceToken } from '@ltvco/refresh-lib/v1';
import { useContext, useEffect, useCallback, useRef } from 'react';

import { Message } from 'components/ChatBV/types';
import {
  API_CONFIG,
  fetchOramLegal,
} from 'routes/Dashboard/CourtCases/v2/shared/hooks';
import {
  type CourtCaseCluster,
  type OpinionResult,
} from 'routes/Dashboard/CourtCases/v2/types';

interface UseCourtOpinionLoaderProps {
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
}

export const useCourtOpinionLoader = ({
  messages,
  setMessages,
}: UseCourtOpinionLoaderProps) => {
  const { logError } = useContext(AppConfig);
  const { data: tokenData, refetch: refetchToken } = useServiceToken('oram');
  const loadingRef = useRef<Set<number>>(new Set());

  const loadCourtOpinionForMessage = useCallback(
    async (message: Message, index: number) => {
      const clusterId = message.courtOpinionClusterId as number | undefined;

      if (
        !clusterId ||
        !tokenData?.token ||
        loadingRef.current.has(clusterId) ||
        message.courtOpinionData
      ) {
        return;
      }

      loadingRef.current.add(clusterId);

      const updateMessage = (updates: Partial<Message>) => {
        setMessages((prevMessages: Message[]) => {
          const updatedMessages = [...prevMessages];
          const currentMessage = updatedMessages[index];
          if (
            currentMessage &&
            currentMessage.courtOpinionClusterId === clusterId
          ) {
            updatedMessages[index] = {
              ...currentMessage,
              ...updates,
            };
          }
          return updatedMessages;
        });
      };

      try {
        const url = `${API_CONFIG.BASE_URL}/legal/clusters/?id=${clusterId}`;
        const data = await fetchOramLegal<{
          results?: CourtCaseCluster[];
        }>(url, tokenData?.token, refetchToken);
        const clusterData = data.results?.[0] || null;

        if (clusterData) {
          const courtOpinionData: OpinionResult = {
            cluster_id: clusterId,
            caseName: clusterData.case_name || '',
            caseNameFull: clusterData.case_name_full || '',
            citation: clusterData.citations || [],
            court: '',
            docketNumber: clusterData.docket_id?.toString() || '',
            dateFiled: clusterData.date_filed || '',
            status: '',
            suitNature: clusterData.nature_of_suit || '',
            judge:
              typeof clusterData.judges === 'string' ? clusterData.judges : '',
            opinions: [],
            syllabus: clusterData.syllabus || '',
            absolute_url: clusterData.absolute_url || '',
            attorney:
              typeof clusterData.attorneys === 'string'
                ? clusterData.attorneys
                : '',
            citeCount: clusterData.citation_count || 0,
            court_citation_string: '',
            docket_id: clusterData.docket_id || 0,
            court_id: '',
            dateArgued: null,
            dateReargued: null,
            dateReargumentDenied: null,
            lexisCite: '',
            neutralCite: '',
            non_participating_judge_ids: [],
            panel_ids: [],
            panel_names: [],
            posture: clusterData.posture || '',
            procedural_history: clusterData.procedural_history || '',
            scdb_id: clusterData.scdb_id || '',
            sibling_ids: [],
            source: clusterData.source || '',
            meta: {
              timestamp: clusterData.date_created || '',
              date_created: clusterData.date_created || '',
              score: {
                bm25: 0,
              },
            },
          };

          updateMessage({
            courtOpinionData,
          });
        } else {
          updateMessage({
            courtOpinionClusterId: undefined,
          });
        }
      } catch (error) {
        logError('Error loading court opinion for message', error as Error);
        updateMessage({
          courtOpinionClusterId: undefined,
        });
      } finally {
        loadingRef.current.delete(clusterId);
      }
    },
    [setMessages, logError, tokenData?.token, refetchToken]
  );

  useEffect(() => {
    messages.forEach((message, index) => {
      const clusterId = message.courtOpinionClusterId;
      if (clusterId && !message.courtOpinionData) {
        loadCourtOpinionForMessage(message, index);
      }
    });
  }, [messages, loadCourtOpinionForMessage]);
};
